import { Commit, Dispatch, Module } from 'vuex'
import { IServicesListState } from './types'
import BigNumber from 'bignumber.js'
import { TIME_HOURLY } from '@/constants/backend/service'
import { Blockchain, ChainId, getNameByWalletGroup } from '@/constants/blockchain'
import { ContractVersion } from '@/constants/contracts'
import { CURRENCY_FIELD_BACKEND_ID, CURRENCY_FIELD_BACKEND_NAME } from '@/constants/currency'
import { SERVICE_DETAILS } from '@/constants/routes'
import Currency from '@/models-ts/Currency'
import CreateContractSignDataV2 from '@/models-ts/sign-process/CreateContractSignDataV2'
import PaymentDetail from '@/models-ts/sign-process/PaymentDetail'
import SignData, {
  IBackendParamsPayToFreelancer,
  IBackendParamsReturnFundsToCustomer,
  TypeSignData,
} from '@/models-ts/sign-process/SignData'
import Wallet from '@/models-ts/Wallet'
import LaborXContractV1, { getLaborXContractV1Async, IPayToFreelancerParamsV1 } from '@/servicies-ts/blockchain/laborx-contract-v1'
import LaborXContractV2, {
  getLaborXContractV2Async,
  IPayToFreelancerParamsV2,
  IRefundToCustomerByCustomerParamsV2,
} from '@/servicies-ts/blockchain/laborx-contract-v2'
import { getPaymentDetailsPayToFreelancerV1 } from '@/servicies-ts/pay-details/pay-details-lx-v1'
import {
  getPaymentDetailsPayToFreelancerV2,
  getPaymentDetailsRefundToCustomerByCustomerV2,
} from '@/servicies-ts/pay-details/pay-details-lx-v2'
import { getBalanceErrors } from '@/utils-ts/contract'
import { getCurrency } from '@/utils/currency'
import { getDateFromString } from '@/utils/date'
import { formatCurrency } from '@/utils/moneyFormat'

const getInitialState = (): IServicesListState => ({
  createContractLoading: {},
  payToFreelancerLoading: {},
  returnFundsToCustomerLoading: {},
})

const gig = (): Module<IServicesListState, any> => {
  return {
    namespaced: true,
    state: getInitialState(),
    mutations: {
      setCreateContractLoading: (state: IServicesListState, { gigId, loading = true }: { gigId: number; loading: boolean }) => {
        state.createContractLoading = {
          ...state.createContractLoading,
          [gigId]: loading,
        }
      },
      setPayToFreelancerLoading: (state: IServicesListState, { gigId, loading = true }: { gigId: number; loading: boolean }) => {
        state.payToFreelancerLoading = {
          ...state.payToFreelancerLoading,
          [gigId]: loading,
        }
      },
      setReturnFundsToCustomerLoading: (state: IServicesListState, { gigId, loading = true }: { gigId: number; loading: boolean }) => {
        state.returnFundsToCustomerLoading = {
          ...state.returnFundsToCustomerLoading,
          [gigId]: loading,
        }
      },
    },
    actions: {
      async signCreateContractV2(
        { commit, dispatch, rootState }: { commit: Commit; dispatch: Dispatch; rootState: any },
        { gig, successSign, cancelSign }: { gig: any; successSign: () => void; cancelSign: () => void }
      ) {
        commit('setCreateContractLoading', { gigId: gig.gig_id })
        const preferredCurrencies = gig.preferredCurrencies
          .map((item: any) =>
            getCurrency({
              blockchain: item.blockchain,
              field: CURRENCY_FIELD_BACKEND_ID,
              value: item.currency,
            })
          )
          .filter(Boolean)
        const wallet: Wallet = rootState.user.wallet
        const rates: any = await dispatch('rates/getRates', true, { root: true })
        const amountUsd = new BigNumber(gig.rate).multipliedBy(gig.time_type === TIME_HOURLY ? gig.hours : 1)
        const freelancer = {
          id: gig.freelancerProfile.id,
          avatar: gig.freelancerProfile.avatar,
          name: gig.freelancerProfile.name,
          type: gig.freelancerProfile.type,
          wallet: gig.freelancer_wallet,
          avgReviews: gig.freelancerProfile.avgReviews,
          reviewsCount: gig.freelancerProfile.reviewsCount,
        }
        const signData = await CreateContractSignDataV2.createInstance({
          version: ContractVersion.v2,
          scId: gig.sc_id,
          preferredCurrencies,
          deadline: gig.deadline,
          wallet,
          rates,
          amountUsd,
          freelancer,
          gigOfferId: gig.id,
          name: gig.name,
          detailsRoute: {
            name: SERVICE_DETAILS,
            params: { id: gig.gig_id, slug: gig.slug },
          },
          successCb: successSign,
          errorCb: () => {},
          cancelCb: cancelSign,
        })
        await dispatch('signProcess/init', { signData }, { root: true })
        commit('setCreateContractLoading', { gigId: gig.gig_id, loading: false })
      },
      async signPayToFreelancerV1(
        { commit, dispatch, rootState }: { commit: Commit; dispatch: Dispatch; rootState: any },
        { gig, successSign }: { gig: any; successSign: (params: {}) => void }
      ) {
        commit('setPayToFreelancerLoading', { gigId: gig.gig_id })
        const blockchain: Blockchain = gig.blockchain
        const currency: Currency = getCurrency({
          blockchain: blockchain,
          field: CURRENCY_FIELD_BACKEND_NAME,
          value: gig.currency,
        })!
        const chainId = Number(ChainId[currency.blockchain])
        const contract: LaborXContractV1 = await getLaborXContractV1Async({ chainId })
        const postfix: 'Eth' | 'Erc20' = currency.isBaseCurrency ? 'Eth' : 'Erc20'
        const wallet: Wallet = rootState.user.wallets.find(
          (item: Wallet) => item.address.toLowerCase() === gig.customerWallet.toLowerCase()
        )
        const methodArgs: IPayToFreelancerParamsV1 = {
          postfix,
          mode: 'encodeABI',
          from: gig.customerWallet,
          contractId: gig.sc_id,
        }
        const gasPrice: string = await contract.web3Instance.eth.getGasPrice()
        const paymentDetails: PaymentDetail[] = await getPaymentDetailsPayToFreelancerV1(methodArgs, contract, currency, gasPrice)
        const backendParams: IBackendParamsPayToFreelancer = {
          postfix,
          blockchain: currency.blockchain,
          gigJobId: gig?.job?.id,
          gigOfferId: gig?.id,
          scId: gig?.sc_id,
        }
        const balance = `${formatCurrency(gig.job.escrow_balance, {
          blockchain: gig.blockchain,
          currency: gig.currency,
        })} ${gig.currency}`
        const { errors } = await getBalanceErrors(currency, wallet, paymentDetails, new BigNumber(0))
        const freelancer = {
          id: gig.freelancerProfile.id,
          avatar: gig.freelancerProfile.avatar,
          name: gig.freelancerProfile.name,
          type: gig.freelancerProfile.type,
          avgReviews: gig.freelancerProfile.avgReviews,
          reviewsCount: gig.freelancerProfile.reviewsCount,
          wallet: gig.freelancer_wallet,
        }
        const signData = new SignData<'payToFreelancer', IPayToFreelancerParamsV1, IBackendParamsPayToFreelancer>({
          version: ContractVersion.v1,
          type: TypeSignData.PayContract,
          name: gig.name,
          title: 'Pay to Freelancer',
          description: `Paying out funds to a freelancer from an escrow.<br>Escrow balance: <b>${balance}</b><br>`,
          freelancer,
          createdAt: getDateFromString(gig.job.created_at),
          deadline: gig.deadline,
          escrowBalance: gig.job.escrow_balance,
          wallet,
          walletChangeable: false,
          blockchain,
          currency,
          payableAmount: '',
          contractName: 'LaborXContractV1',
          paymentDetails,
          contractLink: contract.contractLink(blockchain),
          gasPrice,
          errors,
          backendParams,
          methodName: 'payToFreelancer',
          methodArgs,
          async setGasPrice(gasPrice: string) {
            const self = this as SignData<'payToFreelancer', IPayToFreelancerParamsV1, IBackendParamsPayToFreelancer>
            self.gasPrice = gasPrice
            const contract: LaborXContractV1 = await getLaborXContractV1Async({ chainId })
            self.paymentDetails = await getPaymentDetailsPayToFreelancerV1(methodArgs, contract, currency, gasPrice)
            const { errors } = await getBalanceErrors(self.currency!, self.wallet!, self.paymentDetails, new BigNumber(0))
            self.errors = errors
          },
          successCb: (tx, signData) => {
            return successSign({
              tx,
              walletName: getNameByWalletGroup(signData.wallet.group),
              currency: signData.currency,
              blockchain: signData.blockchain,
            })
          },
          errorCb: () => {},
          cancelCb: () => {},
        })
        await dispatch('signProcess/init', { signData, contract }, { root: true })
        commit('setPayToFreelancerLoading', { gigId: gig.gig_id, loading: false })
      },
      async signPayToFreelancerV2(
        { commit, dispatch, rootState }: { commit: Commit; dispatch: Dispatch; rootState: any },
        { gig, successSign }: { gig: any; successSign: (params: {}) => void }
      ) {
        commit('setPayToFreelancerLoading', { gigId: gig.gig_id })
        const blockchain: Blockchain = gig.blockchain
        const currency: Currency = getCurrency({
          blockchain: blockchain,
          field: CURRENCY_FIELD_BACKEND_NAME,
          value: gig.currency,
        })!
        const contract: any = await getLaborXContractV2Async({ chainId: Number(ChainId[currency.blockchain]) })
        const wallet: Wallet = rootState.user.wallets.find(
          (item: Wallet) => item.address.toLowerCase() === gig.customerWallet.toLowerCase()
        )
        const methodArgs: IPayToFreelancerParamsV2 = {
          mode: 'encodeABI',
          from: gig.customerWallet,
          contractId: gig.sc_id,
        }
        let gasPrice: string | null = null
        let paymentDetails: PaymentDetail[] = []
        gasPrice = await contract.web3Instance.eth.getGasPrice()
        paymentDetails = await getPaymentDetailsPayToFreelancerV2(methodArgs, contract, currency, gasPrice!)
        const backendParams: IBackendParamsPayToFreelancer = {
          blockchain: currency.blockchain,
          gigJobId: gig?.job?.id,
          gigOfferId: gig?.id,
          scId: gig?.sc_id,
        }
        const balance = `${formatCurrency(gig.job.escrow_balance, {
          blockchain: gig.blockchain,
          currency: gig.currency,
        })} ${gig.currency}`
        const { errors } = await getBalanceErrors(currency, wallet, paymentDetails, new BigNumber(0))
        const freelancer = {
          id: gig.freelancerProfile.id,
          avatar: gig.freelancerProfile.avatar,
          name: gig.freelancerProfile.name,
          type: gig.freelancerProfile.type,
          avgReviews: gig.freelancerProfile.avgReviews,
          reviewsCount: gig.freelancerProfile.reviewsCount,
          wallet: gig.freelancer_wallet,
        }
        const signData = new SignData<'payToFreelancer', IPayToFreelancerParamsV2, IBackendParamsPayToFreelancer>({
          version: ContractVersion.v2,
          type: TypeSignData.PayContract,
          name: gig.name,
          title: 'Pay to Freelancer',
          description: `Paying out funds to a freelancer from an escrow.<br>Escrow balance: <b>${balance}</b><br>`,
          freelancer,
          createdAt: getDateFromString(gig.job.created_at),
          deadline: gig.deadline,
          escrowBalance: gig.job.escrow_balance,
          wallet,
          walletChangeable: false,
          blockchain,
          currency,
          payableAmount: '',
          contractName: 'LaborXContractV2',
          paymentDetails,
          contractLink: contract.contractLink(blockchain),
          gasPrice,
          errors,
          backendParams,
          methodName: 'payToFreelancer',
          methodArgs,
          async setGasPrice(gasPrice: string) {
            const self = this as SignData<'payToFreelancer', IPayToFreelancerParamsV2, IBackendParamsPayToFreelancer>
            self.gasPrice = gasPrice
            const contract: LaborXContractV2 = await getLaborXContractV2Async({ chainId: Number(ChainId[self.currency.blockchain]) })
            self.paymentDetails = await getPaymentDetailsPayToFreelancerV2(methodArgs, contract, currency, gasPrice)
            const { errors } = await getBalanceErrors(self.currency!, self.wallet!, self.paymentDetails, new BigNumber(0))
            self.errors = errors
          },
          successCb: (tx, signData) => {
            return successSign({
              tx,
              walletName: getNameByWalletGroup(signData.wallet.group),
              currency: signData.currency,
              blockchain: signData.blockchain,
            })
          },
          errorCb: () => {},
          cancelCb: () => {},
        })
        await dispatch('signProcess/init', { signData, contract }, { root: true })
        commit('setPayToFreelancerLoading', { gigId: gig.gig_id, loading: false })
      },
      async refundToCustomerByCustomerV2(
        { commit, dispatch, rootState }: { commit: Commit; dispatch: Dispatch; rootState: any },
        { gig, successSign }: { gig: any; successSign: () => void }
      ) {
        commit('setReturnFundsToCustomerLoading', { gigId: gig.gig_id })
        const blockchain: Blockchain = gig.blockchain
        const currency: Currency = getCurrency({
          blockchain: blockchain,
          field: CURRENCY_FIELD_BACKEND_NAME,
          value: gig.currency,
        })!
        const contract: any = await getLaborXContractV2Async({ chainId: Number(ChainId[currency.blockchain]) })
        const wallet: Wallet = rootState.user.wallets.find(
          (item: Wallet) => item.address.toLowerCase() === gig.customerWallet.toLowerCase()
        )
        const methodArgs: IRefundToCustomerByCustomerParamsV2 = {
          mode: 'encodeABI',
          from: gig.customerWallet,
          contractId: gig.sc_id,
        }
        let gasPrice: string | null = null
        let paymentDetails: PaymentDetail[] = []
        gasPrice = await contract.web3Instance.eth.getGasPrice()
        paymentDetails = await getPaymentDetailsRefundToCustomerByCustomerV2(methodArgs, contract, currency, gasPrice!)
        const backendParams: IBackendParamsReturnFundsToCustomer = {
          blockchain: currency.blockchain,
          gigJobId: gig?.job?.id,
          gigOfferId: gig?.id,
          scId: gig?.sc_id,
        }
        const balance = `${formatCurrency(gig.job.escrow_balance, {
          blockchain: gig.blockchain,
          currency: gig.currency,
        })} ${gig.currency}`
        const { errors } = await getBalanceErrors(currency, wallet, paymentDetails, new BigNumber(0))
        const freelancer = {
          id: gig.freelancerProfile.id,
          avatar: gig.freelancerProfile.avatar,
          name: gig.freelancerProfile.name,
          type: gig.freelancerProfile.type,
          avgReviews: gig.freelancerProfile.avgReviews,
          reviewsCount: gig.freelancerProfile.reviewsCount,
          wallet: gig.freelancer_wallet,
        }
        const signData = new SignData<
          'refundToCustomerByCustomer',
          IRefundToCustomerByCustomerParamsV2,
          IBackendParamsReturnFundsToCustomer
        >({
          version: ContractVersion.v2,
          type: TypeSignData.Refund,
          name: gig.name,
          title: 'Refund',
          description: `Cancellation of job and refunds from escrow<br>Escrow balance: <b>${balance}</b><br>`,
          freelancer,
          createdAt: getDateFromString(gig.job.created_at),
          deadline: gig.deadline,
          escrowBalance: gig.job.escrow_balance,
          wallet,
          walletChangeable: false,
          blockchain,
          currency,
          payableAmount: '',
          contractName: 'LaborXContractV2',
          paymentDetails,
          contractLink: contract.contractLink(blockchain),
          gasPrice,
          errors,
          backendParams,
          methodName: 'refundToCustomerByCustomer',
          methodArgs,
          async setGasPrice(gasPrice: string) {
            const self = this as SignData<
              'refundToCustomerByCustomer',
              IRefundToCustomerByCustomerParamsV2,
              IBackendParamsReturnFundsToCustomer
            >
            self.gasPrice = gasPrice
            const contract: LaborXContractV2 = await getLaborXContractV2Async({ chainId: Number(ChainId[self.blockchain]) })
            self.paymentDetails = await getPaymentDetailsRefundToCustomerByCustomerV2(methodArgs, contract, currency, gasPrice)
            const { errors } = await getBalanceErrors(self.currency!, self.wallet!, self.paymentDetails, new BigNumber(0))
            self.errors = errors
          },
          successCb: successSign,
          errorCb: () => {},
          cancelCb: () => {},
        })
        await dispatch('signProcess/init', { signData }, { root: true })
        commit('setReturnFundsToCustomerLoading', { gigId: gig.gig_id, loading: false })
      },
    },
  }
}

export default gig
